<template>
  <div
    class="stats"
    :class="{ 'stats--centered': isCentered }"
  >
    <div class="stats__main">
      <div
        v-for="(stat, index) in stats"
        :key="index"
        class="stats__stat"
      >
        <div class="stat__values">
          <p
            v-if="index > 0"
            class="stat__separator"
          ></p>
          <p
            v-if="icons[index]"
            class="stat__icon"
            :style="{ color: `${colors[index]}` }"
          >
            {{ icons[index] }}
          </p>
          <!-- eslint-disable vue/no-v-html -->
          <p
            class="stat__number"
            :class="{ 'stat__number--centered': isCentered }"
            :style="{ color: `${colors[index]}` }"
            v-html="
              formatMemberValue(stat.value, format, FormatContext.STAT, true)
            "
          />
          <!--eslint-enable-->
        </div>
      </div>
    </div>
    <ReflectEvolution
      v-if="shouldDisplayEvolution"
      :stats="stats"
      :is-favorable="isFavorable"
      :format="format"
      :period-type="selectedPeriod!.periodType"
    />
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia'
import { useTimeDimensionStore } from '~/stores/timeDimension'
import { type Stat } from '~/types/analytics'
import { formatMemberValue } from '~/services/analyticsFormat'
import { FormatContext, MemberFormat } from '~/types/analyticsFormat'

export default {
  name: 'Stats',
  props: {
    stats: {
      type: Array as PropType<Stat[]>,
      default: () => []
    },
    icons: {
      type: Array as PropType<string[]>,
      required: true
    },
    colors: {
      type: Array as PropType<string[]>,
      required: true
    },
    format: {
      type: String as PropType<MemberFormat>,
      required: true
    },
    withComparison: {
      type: Boolean,
      default: true
    },
    isFavorable: {
      type: Boolean,
      default: undefined
    },
    isCentered: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    return { formatMemberValue, FormatContext }
  },
  computed: {
    ...mapState(useTimeDimensionStore, ['selectedPeriod']),
    shouldDisplayEvolution() {
      if (
        this.withComparison &&
        this.stats &&
        this.stats.length > 0 &&
        this.stats.every(stat => Number.isFinite(stat.evolution))
      ) {
        return true
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
.stats {
  display: flex;
  flex-direction: column;

  &--centered {
    align-items: center;
    cursor: pointer;
  }

  &__main {
    display: flex;
  }

  &__stat {
    display: flex;

    .stat {
      &__values {
        display: flex;
        align-items: flex-end;
      }

      &__icon {
        @include font-title;
        @include font-size(17px);
        color: $text-tertiary;
        margin: 0 4px 8px 0;
      }

      &__number {
        @include font-title;
        @include font-size($font-size-large);
        color: $text-primary;
        margin-right: $margin-mini;
        white-space: nowrap;

        :deep(.medium) {
          @include font-size(32px);
        }

        :deep(.small) {
          @include font-size(22px);
        }

        &--centered {
          margin-right: 0;
        }
      }

      &__separator {
        @include font-title;
        @include font-size($font-size-intermediate);
        color: $text-primary;
        margin: 0 $margin-mini $margin-small 0;
      }
    }
  }
}
</style>
